import LegalPage from "@/components/legal/LegalPage";

const sections = [
  {
    id: "purpose",
    title: "Purpose of this page",
    body: (
      <>
        <p>
          This page sets out important disclosures about the nature of the
          services Toulios Wealth Management Pty Ltd (&ldquo;Toulios&rdquo;)
          provides, and the limits of any information on this website. It is
          intended to be clear, prominent, and easy to find.
        </p>
        <p>
          By using this website, our tools (including the AI Readiness
          Scorecard) or any of our services, you acknowledge that you have
          read and understood the disclosures below.
        </p>
      </>
    ),
  },
  {
    id: "consulting-only",
    title: "We provide consulting services only",
    body: (
      <>
        <p>
          Toulios Advisory Group (a trading name of Toulios Wealth Management
          Pty Ltd) is a private Australian management consulting firm. Our
          business activity is classified under{" "}
          <strong className="text-white font-medium">ANZSIC code M696299, Management Advice and Related Consulting Services (n.e.c.)</strong>.
          The firm is structured as four divisions: Toulios Growth Systems
          (flagship), Toulios Financial Strategy, Toulios Revenue & Sales,
          and Toulios Operations.
        </p>
        <p>
          We are <strong className="text-white font-medium">not</strong> a
          broker, dealer, fund manager, financial planner, accountant, lawyer
          or licensed financial adviser. We do not deal in or arrange
          financial products. We do not custody client assets. We do not
          execute trades. We do not provide brokerage or any other regulated
          financial service.
        </p>
      </>
    ),
  },
  {
    id: "no-afsl",
    title: "We do not hold an AFSL",
    body: (
      <>
        <p>
          Toulios does not hold, and is not authorised under, an Australian
          Financial Services Licence (AFSL). The services we provide do not
          fall within the licensed activities regulated by the Australian
          Securities and Investments Commission (ASIC) under Chapter 7 of the
          Corporations Act 2001 (Cth).
        </p>
        <p>
          If you require financial product advice, dealing or brokerage, you
          must engage a separate AFSL-holder or authorised representative.
        </p>
      </>
    ),
  },
  {
    id: "general-info-only",
    title: "General information only, not financial product advice",
    body: (
      <>
        <p>
          All content on this website, including text, frameworks,
          scorecards, case studies, downloads, and any commentary, is{" "}
          <strong className="text-white font-medium">general information only</strong>{" "}
          and is provided for educational and informational purposes.
        </p>
        <p>
          Nothing on this website constitutes:
        </p>
        <ul className="list-disc list-outside space-y-2 pl-5 marker:text-gold/70">
          <li>Financial product advice (whether personal or general) as defined under the Corporations Act.</li>
          <li>A recommendation to buy, sell, hold or otherwise transact in any financial product.</li>
          <li>An offer or solicitation of any security, fund, investment scheme or financial product.</li>
          <li>Tax, accounting or legal advice.</li>
        </ul>
        <p>
          Information on this website does not take into account your
          objectives, financial situation or needs. Before acting on any
          information, you should consider whether it is appropriate for you
          and seek independent professional advice from a suitably qualified,
          licensed adviser.
        </p>
      </>
    ),
  },
  {
    id: "case-studies",
    title: "Case studies and examples",
    body: (
      <>
        <p>
          Any case studies, client examples, metrics or outcomes referenced on
          this website are anonymised and indicative. They are based on real
          engagements but have been summarised and, in some cases, generalised
          for the purposes of illustration.
        </p>
        <p>
          Outcomes vary by engagement. We do not represent or guarantee that
          you will obtain similar outcomes from working with us.
        </p>
      </>
    ),
  },
  {
    id: "third-party",
    title: "Third-party references",
    body: (
      <>
        <p>
          References to third-party tools (e.g., Microsoft Power BI), platforms
          or service providers are used solely for descriptive purposes and do
          not imply endorsement or partnership unless explicitly stated.
        </p>
      </>
    ),
  },
  {
    id: "forward-looking",
    title: "Forward-looking statements",
    body: (
      <>
        <p>
          Where our materials contain forward-looking statements, projections,
          targets, scenarios, illustrative models, these are based on
          assumptions that may prove inaccurate. Actual results may differ
          materially. We do not undertake to update forward-looking statements
          to reflect later events.
        </p>
      </>
    ),
  },
  {
    id: "conflicts",
    title: "Conflicts of interest",
    body: (
      <>
        <p>
          From time to time, our principals may hold personal investments in
          listed or private companies. Where there is a meaningful risk that
          such a holding could create a conflict with an engagement, we will
          disclose it before accepting the engagement and, where appropriate,
          decline the work or implement an appropriate management protocol.
        </p>
      </>
    ),
  },
  {
    id: "jurisdiction",
    title: "Jurisdiction",
    body: (
      <>
        <p>
          Information on this website is intended for an Australian audience.
          We make no representation that the content is appropriate for use in
          other jurisdictions. If you access this website from outside
          Australia, you do so at your own initiative and are responsible for
          compliance with local laws.
        </p>
      </>
    ),
  },
  {
    id: "questions",
    title: "Questions",
    body: (
      <>
        <p>
          If anything on this page is unclear, please reach out via the{" "}
          <a className="text-gold hover:underline" href="/contact">Contact</a>{" "}
          page or at <span className="text-white">hello@touliosWealth.com</span>.
        </p>
      </>
    ),
  },
];

export default function Disclosures() {
  return (
    <LegalPage
      eyebrow="Disclosures"
      title="Disclosures."
      lastUpdated="20 May 2026"
      summary="Important context about what we do, what we don't, and how to interpret the content and tools on this website."
      sections={sections}
    />
  );
}
